import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

interface ProtectedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  imageUrl: string;
  placeholderUrl?: string;
  sx?: React.CSSProperties;
  aspectRatio?: string | number;
  objectFit?: React.CSSProperties['objectFit'];
}

const defaultPlaceholderUrl = '/images/404.png';

const ProtectedImage: React.FC<ProtectedImageProps> = ({
  imageUrl,
  placeholderUrl = defaultPlaceholderUrl,
  alt,
  aspectRatio,
  objectFit = 'cover',
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const fetchProtectedImage = async () => {
      try {
        // For user content images, we need to make a direct fetch with auth token
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found');
        }

        const response = await fetch(imageUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status}`);
        }

        const blob = await response.blob();
        const objectURL = URL.createObjectURL(blob);
        setImageSrc(objectURL);
        setError(false);
      } catch (error) {
        console.error('Error loading image:', error);
        setError(true);
      }
    };

    fetchProtectedImage();

    // Clean up the object URL on component unmount
    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageUrl]);

  const imageStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit,
  };

  if (aspectRatio) {
    imageStyle.aspectRatio = aspectRatio;
  }

  if (error) {
    return <img {...props} src={placeholderUrl} alt={alt || 'Image not found'} style={imageStyle} />;
  }

  return (
    <Box
      component="img"
      src={imageSrc || placeholderUrl}
      alt={alt}
      {...props}
      sx={{
        ...props.sx,
        ...imageStyle,
      }}
    />
  );
};

export default ProtectedImage;